import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { Button } from "@/components/ui/button";
import { BrandHeader } from "@/components/forms/BrandHeader";
import { FieldInput } from "@/components/forms/FieldInput";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function FillForm() {
  const { id, sid } = useParams();
  const nav = useNavigate();
  const ret = useSearchParams()[0].get("return");
  const back = ret && /^\/app\/[\w/-]*$/.test(ret) ? ret : "/app/forms";
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState("");

  useEffect(() => {
    const fail = (e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/app/forms"); };
    if (sid) {
      api.get(`/form-submissions/${sid}`).then(async (r) => {
        const s = r.data; let branding = null;
        try { branding = (await api.get(`/forms/${s.form_id}`)).data.branding; } catch { /* form may be unassigned now */ }
        setForm({ title: s.form_title, fields: s.fields, branding, editing: s, locked: (new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`) - new Date(`${(s.submitted_at || "").slice(0, 10)}T00:00:00Z`)) / 86400000 > 7 }); setAnswers(s.answers || {});
      }).catch(fail);
    } else { api.get(`/forms/${id}`).then((r) => setForm(r.data)).catch(fail); }
  }, [id, sid, nav]);

  const submit = async () => {
    const isEmpty = (v) => v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);
    const missing = form.fields.find((f) => f.required && f.type !== "heading" && isEmpty(answers[f.id]));
    if (missing) { toast.error(`'${missing.label}' is required`); document.querySelector(`[data-field="${missing.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    setBusy(true);
    try {
      if (sid) {
        if (!why.trim()) { toast.error("Say what you corrected"); return; }
        await api.put(`/form-submissions/${sid}`, { answers, reason: why });
        toast.success("Submission updated");
        nav("/app/forms");
        return;
      }
      const { data } = await api.post(`/forms/${id}/submit`, { answers });
      if (data.has_fail) toast.warning("Submitted. Your manager has been alerted to the failed items."); else toast.success("Submitted. Thank you!");
      nav(back);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <EmployeeLayout title="Form">
      <Link to={back} className="inline-flex items-center gap-1 text-sm text-zinc-500 mb-3" data-testid="fill-back"><ArrowLeft className="h-4 w-4" />{back === "/app/forms" ? "Forms" : "Back"}</Link>
      {!form ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div> : (
        <div className="space-y-3">
          <BrandHeader branding={form.branding} title={form.title} description={form.description} />
          {form.editing && <div data-testid="fill-editing" className="text-xs bg-amber-50 text-amber-800 rounded-md px-3 py-2">{form.locked ? `Submitted ${(form.editing.submitted_at || "").slice(0, 10)}. It's more than 7 days old, so ask your Admin if anything needs correcting.` : `Editing your submission from ${(form.editing.submitted_at || "").slice(0, 10)}. Changes are kept in the history.`}</div>}
          {!form.editing && form.done && <div data-testid="fill-already-done" className="text-xs bg-emerald-50 text-emerald-800 rounded-md px-3 py-2 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" />Already completed for this period. You can submit again if needed.</div>}
          {form.fields.map((f) => f.type === "heading"
            ? <div key={f.id} className="text-xs font-bold uppercase tracking-wider text-zinc-500 pt-3">{f.label}</div>
            : (
              <div key={f.id} data-field={f.id} className="bg-white border border-zinc-200 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-900 mb-1.5">{f.label}{f.required && <span className="text-rose-500"> *</span>}</div>
                {f.help && <p className="text-xs text-zinc-400 -mt-1 mb-2">{f.help}</p>}
                <fieldset disabled={!!form.locked} className="contents"><FieldInput field={f} value={answers[f.id]} onChange={(v) => setAnswers((p) => ({ ...p, [f.id]: v }))} /></fieldset>
              </div>
            ))}
          {sid && !form.locked && <div className="bg-white border border-zinc-200 rounded-lg p-3"><div className="text-sm font-medium mb-1.5">What did you correct? <span className="text-rose-500">*</span></div><textarea data-testid="fill-edit-reason" value={why} onChange={(e) => setWhy(e.target.value)} rows={2} className="w-full border border-zinc-200 rounded-md p-2 text-sm" placeholder="e.g. Wrong signature name" /></div>}
          {!form.locked && <Button data-testid="fill-submit" disabled={busy} onClick={submit} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : sid ? "Save changes" : "Submit"}</Button>}
        </div>
      )}
    </EmployeeLayout>
  );
}
